import type { ExportJobDetail, ExportJobItem } from "@/types/api";

/** Wireframe display titles for mock jobs */
export const MOCK_EXPORT_DISPLAY_TITLES: Record<string, string> = {
  job_20260520_a1b2: "May 20 · Paid invoices",
  job_20260520_c3d4: "May 20 · All open",
  job_20260519_e5f6: "May 19 · Selected rows",
  job_20260518_x9y8: "May 18 · Acme Corp",
  job_mock_ui_preview: "May 20 · Selected rows",
};

export const MOCK_EXPORT_ZIP_SIZE: Record<string, string> = {
  job_20260520_c3d4: "2.1 MB",
  job_20260518_x9y8: "890 KB",
  job_mock_ui_preview: "128 KB",
};

export const MOCK_EXPORT_NOTIFY_EMAIL: Record<string, string> = {
  job_20260520_c3d4: "finance@acme.com",
};

const SAMPLE_ITEMS: ExportJobItem[] = [
  {
    invoice_id: "in_1Px7kMay",
    item_status: "SUCCESS",
    customer_id: "cus_RAcme01",
    customer_name: "Acme Corp",
    invoice_number: "INV-2026-0042",
    invoice_created_at: "2026-05-20T08:00:00Z",
    amount_due: 4500,
    currency: "usd",
    remark: null,
  },
  {
    invoice_id: "in_1Py2mMay",
    item_status: "FAILED",
    customer_id: "cus_RBeta02",
    customer_name: "Beta Example",
    invoice_number: "INV-2026-0044",
    invoice_created_at: "2026-05-19T14:00:00Z",
    amount_due: 1200,
    currency: "usd",
    remark: "PDF unavailable (not finalized)",
  },
];

export const MOCK_EXPORT_JOB_ITEMS: Record<string, ExportJobItem[]> = {
  job_20260520_c3d4: SAMPLE_ITEMS,
  job_mock_ui_preview: SAMPLE_ITEMS.slice(0, 1),
  job_20260519_e5f6: SAMPLE_ITEMS.map((row) => ({
    ...row,
    item_status: "FAILED" as const,
    remark: row.remark ?? "Export failed",
  })),
};

export const MOCK_EXPORT_JOBS: ExportJobDetail[] = [
  {
    job_id: "job_20260520_a1b2",
    status: "RUNNING",
    source: "selection",
    invoice_count: 15,
    invoice_success_count: 8,
    invoice_failed_count: 0,
    progress_percent: 53,
    notify_status: "NOT_SENT",
    created_at: "2026-05-20T09:12:00Z",
    finished_at: null,
    duration_ms: null,
    error_code: null,
    error_message: null,
  },
  {
    job_id: "job_20260520_c3d4",
    status: "SUCCESS",
    source: "selection",
    invoice_count: 24,
    invoice_success_count: 24,
    invoice_failed_count: 0,
    progress_percent: 100,
    notify_status: "SENT",
    created_at: "2026-05-20T08:40:00Z",
    finished_at: "2026-05-20T08:52:00Z",
    duration_ms: 720_000,
    error_code: null,
    error_message: null,
    items: SAMPLE_ITEMS,
  },
  {
    job_id: "job_20260519_e5f6",
    status: "FAILED",
    source: "selection",
    invoice_count: 30,
    invoice_success_count: 0,
    invoice_failed_count: 30,
    progress_percent: 100,
    notify_status: "NOT_SENT",
    created_at: "2026-05-19T22:01:00Z",
    finished_at: "2026-05-19T22:02:00Z",
    duration_ms: 8_000,
    error_code: "ZIP_PARTIAL",
    error_message: "4 invoices could not be downloaded",
    items: MOCK_EXPORT_JOB_ITEMS.job_20260519_e5f6,
  },
  {
    job_id: "job_20260518_x9y8",
    status: "SUCCESS",
    source: "from_filters",
    invoice_count: 12,
    invoice_success_count: 12,
    invoice_failed_count: 0,
    progress_percent: 100,
    notify_status: "NOT_SENT",
    created_at: "2026-05-18T14:22:00Z",
    finished_at: "2026-05-18T14:23:00Z",
    error_code: null,
    error_message: null,
  },
  {
    job_id: "job_20260528_filters",
    status: "FAILED",
    source: "from_filters",
    invoice_count: 0,
    invoice_success_count: 0,
    invoice_failed_count: 0,
    progress_percent: 0,
    notify_status: "NOT_SENT",
    created_at: "2026-05-28T10:53:00Z",
    finished_at: "2026-05-28T10:53:01Z",
    duration_ms: 1_200,
    error_code: "INVALID_PARAM",
    error_message: "stripe_account_id not set",
    filter_summary: "Created: May 1, 2026 – May 31, 2026",
    filter_snapshot_json: '{"created_from":1775001600,"created_to":1780185599}',
  },
  {
    job_id: "job_mock_ui_preview",
    status: "SUCCESS",
    source: "selection",
    invoice_count: 1,
    invoice_success_count: 1,
    invoice_failed_count: 0,
    progress_percent: 100,
    notify_status: "NOT_SENT",
    created_at: "2026-05-20T10:00:00Z",
    finished_at: "2026-05-20T10:00:05Z",
    error_code: null,
    error_message: null,
    items: MOCK_EXPORT_JOB_ITEMS.job_mock_ui_preview,
  },
];

export function mockExportDisplayTitle(job: ExportJobDetail): string {
  return MOCK_EXPORT_DISPLAY_TITLES[job.job_id] ?? job.job_id;
}

export function mockExportJobById(jobId: string): ExportJobDetail | undefined {
  const job = MOCK_EXPORT_JOBS.find((j) => j.job_id === jobId);
  if (!job) return undefined;
  return {
    ...job,
    items: job.items ?? MOCK_EXPORT_JOB_ITEMS[jobId] ?? [],
  };
}

export function mockExportJobsList(
  page = 0,
  pageSize = 10,
): import("@/types/api").ExportJobListResponse {
  const start = page * pageSize;
  const items = MOCK_EXPORT_JOBS.slice(start, start + pageSize);
  return {
    items,
    page,
    page_size: pageSize,
    has_more: start + pageSize < MOCK_EXPORT_JOBS.length,
  };
}

export function mockExportZipSize(job: ExportJobDetail): string | null {
  return MOCK_EXPORT_ZIP_SIZE[job.job_id] ?? null;
}

export function mockExportNotifyEmail(job: ExportJobDetail): string | null {
  return MOCK_EXPORT_NOTIFY_EMAIL[job.job_id] ?? null;
}

export function formatNotifyStatusLabel(
  status: import("@/types/api").NotifyStatus,
): string {
  if (status === "SENT") return "Sent";
  if (status === "FAILED") return "Failed";
  if (status === "NOT_SENT") return "Pending";
  return status;
}

export function notifyStatusVariant(
  status: import("@/types/api").NotifyStatus,
): "running" | "ready" | "failed" | "neutral" {
  if (status === "SENT") return "ready";
  if (status === "FAILED") return "failed";
  if (status === "NOT_SENT") return "running";
  return "neutral";
}
